import { Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { FormHook } from '../hook/useForm';
import { ClusterFilters } from '../models/interfaces';
import { FilterWrapper } from '../shared/all.styles';
import { Typography } from '../shared/typography';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { useEffect, useState } from 'react';

const FILTER_STORE_KEY = 'clusters';

interface Props {
  clearFilters: () => void;
  formProps: FormHook<ClusterFilters>;
  hideStatus?: boolean;
  defaultFilters: ClusterFilters;
}

const ClustersFilters = ({
  clearFilters,
  formProps: { bindTextField, submit, setValue, form },
}: Props) => {
  const { t } = useTranslation();

  const [cookie, setCookie] = useCookies([`fltrs-${FILTER_STORE_KEY}`]);

  const [hasReviwString, setHasReviewString] = useState<string>('all');
  const [managedByGksdString, setManagedByGksdString] = useState<string>('all');
  const [fusionString, setFusionString] = useState<string>('all');

  const handleSubmit = () => {
    setCookie(`fltrs-${FILTER_STORE_KEY}`, form);
    submit();
  };

  useEffect(() => {
    if (cookie[`fltrs-${FILTER_STORE_KEY}`]) {
      form = cookie[`fltrs-${FILTER_STORE_KEY}`];
      const hasReview = cookie[`fltrs-${FILTER_STORE_KEY}`]?.has_review;
      switch (hasReview) {
        case true:
          setHasReviewString('yes');
          break;
        case false:
          setHasReviewString('no');
          break;
        default:
          setHasReviewString('all');
          break;
      }
      const managedByGksd = cookie[`fltrs-${FILTER_STORE_KEY}`]?.managed_by_gksd;
      switch (managedByGksd) {
        case true:
          setManagedByGksdString('yes');
          break;
        case false:
          setManagedByGksdString('no');
          break;
        default:
          setManagedByGksdString('all');
          break;
      }
      const fusion = cookie[`fltrs-${FILTER_STORE_KEY}`]?.fusion_main;
      switch (fusion) {
        case true:
          setFusionString('yes');
          break;
        case false:
          setFusionString('no');
          break;
        default:
          setFusionString('all');
          break;
      }
    }
  }, [cookie]);

  const handleClearFilters = () => {
    setCookie(`fltrs-${FILTER_STORE_KEY}`, {});
    clearFilters();
  };

  return (
    <>
      <FilterWrapper grayBackground>
        <Stack direction={'row'} spacing={3} alignItems={'center'}>
          <Typography variant="subtitle1">{t('cluster.filters')}</Typography>
          <Box
            sx={{
              width: '1px',
              backgroundColor: 'gray',
              height: '40px',
              display: 'block',
              opacity: 0.8,
            }}
          />
          <Stack direction={'row'} spacing={3} alignItems={'center'} width={'100%'}>
            <TextField
              {...bindTextField({ field: 'cluster_id' })}
              label={t('cluster.id')}
              fullWidth
              sx={{ maxWidth: '35%', borderRadius: '30px' }}
            />
            <TextField
              {...bindTextField({ field: 'full_text' })}
              label={t('cluster.descriptionContains')}
              fullWidth
              sx={{ maxWidth: '40%', borderRadius: '30px' }}
            />
            <TextField
              label={t('cluster.toReview')}
              fullWidth
              sx={{ maxWidth: '40%', borderRadius: '30px' }}
              value={hasReviwString}
              select
              onChange={(e) => {
                const val = e.target.value;
                switch (val) {
                  case 'yes':
                    setValue({ field: 'has_review', value: true });
                    break;
                  case 'no':
                    setValue({ field: 'has_review', value: false });
                    break;
                  case 'all':
                    setValue({ field: 'has_review', value: undefined });
                    break;
                }
                setHasReviewString(val);
              }}
            >
              <MenuItem key={'yes'} value={'yes'}>
                <Typography truncate>{t('info.yes')}</Typography>
              </MenuItem>
              <MenuItem key={'no'} value={'no'}>
                <Typography truncate>{t('info.no')}</Typography>
              </MenuItem>
              <MenuItem key={'all'} value={'all'}>
                <Typography truncate>{t('info.all')}</Typography>
              </MenuItem>
            </TextField>
            <TextField
              label={t('cluster.managedByGksd')}
              fullWidth
              sx={{ maxWidth: '40%', borderRadius: '30px' }}
              value={managedByGksdString}
              select
              onChange={(e) => {
                const val = e.target.value;
                switch (val) {
                  case 'yes':
                    setValue({ field: 'managed_by_gksd', value: true });
                    break;
                  case 'no':
                    setValue({ field: 'managed_by_gksd', value: false });
                    break;
                  case 'all':
                    setValue({ field: 'managed_by_gksd', value: undefined });
                    break;
                }
                setManagedByGksdString(val);
              }}
            >
              <MenuItem key={'yes'} value={'yes'}>
                <Typography truncate>{t('info.yes')}</Typography>
              </MenuItem>
              <MenuItem key={'no'} value={'no'}>
                <Typography truncate>{t('info.no')}</Typography>
              </MenuItem>
              <MenuItem key={'all'} value={'all'}>
                <Typography truncate>{t('info.all')}</Typography>
              </MenuItem>
            </TextField>
            <TextField
              label={t('cluster.fusion')}
              fullWidth
              sx={{ maxWidth: '40%', borderRadius: '30px' }}
              value={fusionString}
              select
              onChange={(e) => {
                const val = e.target.value;
                switch (val) {
                  case 'yes':
                    setValue({ field: 'fusion_main', value: true });
                    break;
                  case 'no':
                    setValue({ field: 'fusion_main', value: false });
                    break;
                  case 'all':
                    setValue({ field: 'fusion_main', value: undefined });
                    break;
                }
                setFusionString(val);
              }}
            >
              <MenuItem key={'yes'} value={'yes'}>
                <Typography truncate>{t('info.yes')}</Typography>
              </MenuItem>
              <MenuItem key={'no'} value={'no'}>
                <Typography truncate>{t('info.no')}</Typography>
              </MenuItem>
              <MenuItem key={'all'} value={'all'}>
                <Typography truncate>{t('info.all')}</Typography>
              </MenuItem>
            </TextField>
          </Stack>
          <Stack direction={'row'}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClearFilters}
              sx={{ mr: '30px', whiteSpace: 'nowrap' }}
            >
              {t('actions.clearFilters')}
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              {t('actions.search')}
            </Button>
          </Stack>
        </Stack>
      </FilterWrapper>
    </>
  );
};

export default ClustersFilters;
